import { debounce, Extensions, Toast } from '@onbeefapp/constants';
import React from 'react';
import { useGetProductVideo } from '../queries/video';
import { ProductVideo } from '../queries/video/types';
import { useMerchantStore } from '../stores/merchant';
import MuxPlayer from '@mux/mux-player-react';
import {
  ShareIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  Square2StackIcon,
} from '@heroicons/react/24/outline';
import cx from 'classnames';

export const useVideoWidget = (product_id?: string) => {
  const merchant = useMerchantStore((state) => state.merchant);
  const { mutateAsync: getProductVideo } = useGetProductVideo();
  const [video, setVideo] = React.useState<ProductVideo | null>(null);

  const fetchAvailableVideo = debounce(
    async (merchant_id: string, product_id: string) => {
      try {
        const res = await getProductVideo({
          merchant_id,
          product_id,
        });
        setVideo(res.video);
      } catch (error) {
        console.log(error);
      }
    },
    1000,
  );

  React.useEffect(() => {
    if (!merchant) return;

    const enabled = merchant.active_plan_extensions?.includes(
      Extensions.PRODUCT_VIDEO,
    );
    if (!product_id || !enabled) return setVideo(null);

    fetchAvailableVideo(merchant.id, product_id);
  }, [merchant, product_id]);

  return { VideoWidget: <VideoWidgetComponent video={video} /> };
};

const VideoWidgetComponent: React.FC<{ video: ProductVideo | null }> = ({
  video,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(true);
  const [showControls, setShowControls] = React.useState(true);
  const [progress, setProgress] = React.useState(0);

  const muxPlayerRef = React.useRef<any>(null);
  const controlsTimeoutRef = React.useRef<NodeJS.Timeout>();

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const updateProgressSmooth = React.useCallback(() => {
    if (muxPlayerRef.current && isOpen && !muxPlayerRef.current.paused) {
      const player = muxPlayerRef.current;

      if (player.duration && player.currentTime !== undefined) {
        const percent = (player.currentTime / player.duration) * 100;
        setProgress(percent);
      }

      requestAnimationFrame(updateProgressSmooth);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && muxPlayerRef.current) {
      const player = muxPlayerRef.current;

      const handlePlay = () => {
        updateProgressSmooth();
      };

      const handlePause = () => {};

      const handleEnded = () => {
        setProgress(100);
      };

      player.addEventListener('play', handlePlay);
      player.addEventListener('pause', handlePause);
      player.addEventListener('ended', handleEnded);

      if (!player.paused) {
        updateProgressSmooth();
      }

      return () => {
        player.removeEventListener('play', handlePlay);
        player.removeEventListener('pause', handlePause);
        player.removeEventListener('ended', handleEnded);
      };
    }
  }, [isOpen, updateProgressSmooth]);

  const resetControlsTimeout = React.useCallback(() => {
    if (!isOpen) return;

    setShowControls(true);

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && !isMobile) {
      document.addEventListener('mousemove', resetControlsTimeout);
      document.addEventListener('touchstart', resetControlsTimeout);

      return () => {
        document.removeEventListener('mousemove', resetControlsTimeout);
        document.removeEventListener('touchstart', resetControlsTimeout);
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      };
    }
  }, [isOpen, isMobile, resetControlsTimeout]);

  const handleOpenPlayer = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setProgress(0);
    resetControlsTimeout();
    muxPlayerRef.current.play();
  };

  const handleClosePlayer = () => {
    setIsOpen(false);
    setIsMinimized(true);
    setShowControls(true);
    setProgress(0);
    muxPlayerRef.current.pause();
  };

  const handlePlayerClick = () => {
    if (muxPlayerRef.current) {
      if (muxPlayerRef.current.paused) {
        muxPlayerRef.current.play();
      } else {
        muxPlayerRef.current.pause();
      }
    }
    resetControlsTimeout();
  };

  const handleToggleSound = () => {
    setIsMuted(!isMuted);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Confira este produto!',
          url: window.location.href,
        });
      } catch (err) {}
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      Toast.success('Link copiado com sucesso');
    } catch (err) {
      Toast.error('Não foi possível copiar o link');
    }
  };

  if (!video) return null;

  return (
    <>
      {isMinimized && (
        <div
          className={`
                        fixed z-[9998] cursor-pointer transition-all duration-300 select-none hover:scale-105
                        ${
                          isMobile
                            ? 'w-16 h-16 bottom-20 left-4 cursor-grab '
                            : 'w-20 h-20 bottom-4 left-4'
                        }
                        rounded-full overflow-hidden shadow-lg border-2 border-white
                    `}
          onClick={handleOpenPlayer}
        >
          <img
            src={video.preview_url}
            alt="Video preview"
            className="w-full h-full object-cover pointer-events-none"
            draggable={false}
          />

          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all"></div>
        </div>
      )}

      <div
        className={cx(
          isOpen ? 'fixed' : 'hidden',
          'inset-0 z-[9998] flex items-center justify-center md:items-end md:justify-start pointer-events-none',
        )}
      >
        <div className="relative bg-transparent w-full md:w-1/3 lg:w-1/4 h-full md:h-2/3 md:m-4">
          <div className="absolute top-4 left-4 right-4 h-1 bg-white bg-opacity-30 rounded-full z-30">
            <div
              className="h-full bg-white rounded-full transition-none"
              style={{
                width: `${progress}%`,
                transform: 'translateZ(0)',
                willChange: 'width',
              }}
            />
          </div>

          <div
            className="relative w-full h-full cursor-pointer pointer-events-auto"
            onClick={handlePlayerClick}
          >
            <MuxPlayer
              ref={muxPlayerRef}
              playbackId={video.playback_id}
              muted={isMuted}
              loop={true}
              controls={false}
              disablePictureInPicture={true}
              disableTracking={true}
              noHotkeys={true}
              fullScreen={true}
              disableRemotePlayback={true}
              style={
                {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  background: 'black',
                  '--controls': 'none',
                  '--center-controls': 'none',
                  '--bottom-controls': 'none',
                  '--top-controls': 'none',
                  '--media-object-fit': 'cover',
                  '--media-object-position': 'center',
                } as React.CSSProperties
              }
              primaryColor="transparent"
              secondaryColor="transparent"
              accentColor="transparent"
            />
          </div>

          <div
            className={`
                            absolute inset-0 transition-opacity duration-300 pointer-events-none
                            ${
                              showControls || isMobile
                                ? 'opacity-100'
                                : 'opacity-0'
                            }
                        `}
          >
            <div className="absolute top-2 left-0 right-0 p-4 z-20">
              <div className="flex justify-end">
                <button
                  onClick={handleClosePlayer}
                  className="w-8 h-8 flex items-center justify-center text-white pointer-events-auto"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div
              className={`
                                absolute flex flex-col space-y-4 pointer-events-auto z-20
                                ${
                                  isMobile
                                    ? 'right-4 bottom-24'
                                    : 'right-4 bottom-4'
                                }
                            `}
            >
              <button
                onClick={handleToggleSound}
                className="w-11 h-11 bg-black bg-opacity-60 rounded-full flex items-center justify-center text-white hover:bg-opacity-80 transition-all backdrop-blur-sm"
              >
                {isMuted ? (
                  <SpeakerXMarkIcon className="w-5 h-5 stroke-2" />
                ) : (
                  <SpeakerWaveIcon className="w-5 h-5 stroke-2" />
                )}
              </button>

              <button
                onClick={handleShare}
                className="w-11 h-11 bg-black bg-opacity-60 rounded-full flex items-center justify-center text-white hover:bg-opacity-80 transition-all backdrop-blur-sm"
              >
                <ShareIcon className="w-5 h-5 stroke-2" />
              </button>

              <button
                onClick={handleCopyLink}
                className="w-11 h-11 bg-black bg-opacity-60 rounded-full flex items-center justify-center text-white hover:bg-opacity-80 transition-all backdrop-blur-sm"
              >
                <Square2StackIcon className="w-5 h-5 stroke-2" />
              </button>
            </div>

            <div className="absolute bottom-4 left-4 right-4 text-center">
              <p className="text-white text-xs opacity-60">
                {isMobile ? 'Toque' : 'Clique'} para pausar
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
